import React, { useEffect, useState } from "react";
import { Menu, X, MapPin } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import content from "../content";

const LOGO =
    "https://static.wixstatic.com/media/913191_d541bffa8fc14b0783211e8bb135687e~mv2.png/v1/fill/w_153,h_155,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/logo%20300px%20%2B%20texte%20%2B%20marron.png";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    // Seule la page d'accueil "/" a un hero sombre : les autres pages ont un fond crème → navbar toujours en mode clair.
    const isHome = location.pathname === "/";

    const { nav, contact, brand } = content;
    const links = nav.links;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const onDark = isHome && !scrolled;
    // Sur les pages intérieures (galerie, bon-cadeau), la navbar adopte toujours le style "scrolled" (fond crème, texte sombre).
    const showSolidBg = scrolled || !isHome;

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                showSolidBg
                    ? "bg-[#F9F8F6]/95 backdrop-blur-md border-b border-brand-line shadow-sm"
                    : "bg-transparent"
            }`}
        >
            {/* Mobile top info bar */}
            <div
                data-testid="mobile-topbar"
                className={`sm:hidden transition-colors duration-500 ${
                    showSolidBg ? "bg-[#2C1E16] text-[#F9F8F6]" : "bg-black/45 text-white backdrop-blur-sm"
                }`}
            >
                <div className="px-4 h-8 flex items-center justify-between gap-3 text-[10px] tracking-[0.12em] uppercase">
                    <a
                        href={contact.mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="mobile-topbar-address"
                        className="truncate hover:text-[#D2B48C] transition-colors"
                    >
                        {contact.addressLine1} · {contact.addressLine2}
                    </a>
                    <a
                        href={`tel:${contact.phoneIntl}`}
                        data-testid="mobile-topbar-phone"
                        className="shrink-0 font-semibold hover:text-[#D2B48C] transition-colors"
                    >
                        {contact.phone}
                    </a>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-5 sm:px-10 lg:px-16">
                <div className="flex items-center justify-between gap-4 h-24">
                    {/* LEFT — Logo + Brand name */}
                    <Link
                        to="/"
                        data-testid="nav-logo-link"
                        className="flex items-center gap-3 sm:gap-4 group shrink-0"
                    >
                        <span
                            className={`inline-flex items-center justify-center h-14 w-14 sm:h-16 sm:w-16 rounded-full transition-all duration-500 ${
                                onDark
                                    ? "bg-[#F9F8F6] shadow-lg ring-1 ring-white/40"
                                    : "bg-transparent"
                            }`}
                        >
                            <img
                                src={LOGO}
                                alt={brand.name}
                                className="h-11 w-11 sm:h-12 sm:w-12 object-contain"
                            />
                        </span>
                        <span
                            className={`font-brand text-xl sm:text-3xl tracking-wide leading-none transition-colors ${
                                onDark ? "text-white drop-shadow-md" : "text-brand-primary"
                            }`}
                        >
                            {brand.name}
                        </span>
                    </Link>

                    {/* CENTER — Desktop nav */}
                    <nav className="hidden xl:flex items-center gap-8">
                        {links.map((l) => (
                            <Link
                                key={l.href}
                                to={l.href}
                                data-testid={`nav-link-${l.href.replace(/[/#]/g, "")}`}
                                className={`whitespace-nowrap text-xs tracking-[0.18em] uppercase font-medium transition-colors hover:text-[#D2B48C] ${
                                    onDark ? "text-white/95 drop-shadow" : "text-brand-primary"
                                }`}
                            >
                                {l.label}
                            </Link>
                        ))}
                    </nav>

                    {/* RIGHT — Address + Map button + Reserve / Burger */}
                    <div className="flex items-center gap-3 sm:gap-5 shrink-0">
                        <a
                            href={contact.mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="nav-address"
                            className={`hidden sm:flex flex-col items-end leading-tight transition-colors hover:text-[#D2B48C] ${
                                onDark ? "text-white/95 drop-shadow" : "text-brand-primary"
                            }`}
                        >
                            <span className="text-[11px] sm:text-xs tracking-[0.18em] uppercase font-semibold">
                                {contact.addressLine1}
                            </span>
                            <span className="text-[11px] sm:text-xs tracking-[0.18em] uppercase font-semibold mt-0.5">
                                {contact.addressLine2}
                            </span>
                            <span
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.location.href = `tel:${contact.phoneIntl}`;
                                }}
                                className="text-[11px] sm:text-xs tracking-[0.18em] uppercase font-semibold mt-0.5 hover:underline"
                                data-testid="nav-phone"
                            >
                                {contact.phone}
                            </span>
                        </a>

                        <a
                            href={contact.mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="nav-map-btn"
                            aria-label="Voir sur Google Maps"
                            title="Voir sur Google Maps"
                            className={`group relative inline-flex h-10 w-10 items-center justify-center rounded-full border transition-all duration-300 ${
                                onDark
                                    ? "border-white/40 text-white hover:bg-white hover:text-[#5C4033] hover:border-white"
                                    : "border-[#5C4033]/40 text-[#5C4033] hover:bg-[#5C4033] hover:text-[#F9F8F6] hover:border-[#5C4033]"
                            }`}
                        >
                            <MapPin size={18} strokeWidth={1.8} />
                        </a>

                        <span
                            aria-hidden="true"
                            className={`hidden sm:block h-9 w-px ${
                                onDark ? "bg-white/30" : "bg-brand-line"
                            }`}
                        />

                        <a
                            href={contact.reservationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="nav-reserve-btn"
                            className="hidden lg:inline-flex items-center justify-center px-6 py-2.5 bg-[#5C4033] text-[#F9F8F6] text-xs uppercase tracking-[0.22em] font-semibold border border-[#5C4033] hover:bg-transparent hover:text-[#5C4033] transition-all duration-300"
                        >
                            {nav.reserveBtn}
                        </a>

                        <button
                            type="button"
                            data-testid="mobile-menu-toggle"
                            aria-label="Menu"
                            onClick={() => setOpen((v) => !v)}
                            className={`lg:hidden p-2 -mr-2 transition-colors ${
                                onDark ? "text-white" : "text-brand-primary"
                            }`}
                        >
                            {open ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden bg-[#F9F8F6] border-t border-brand-line"
                >
                    <nav className="px-6 py-6 flex flex-col gap-5">
                        {links.map((l) => (
                            <Link
                                key={l.href}
                                to={l.href}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-link-${l.href.replace(/[/#]/g, "")}`}
                                className="text-sm uppercase tracking-[0.2em] text-brand-primary"
                            >
                                {l.label}
                            </Link>
                        ))}
                        <a
                            href={contact.reservationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="mobile-reserve-btn"
                            className="mt-2 inline-flex items-center justify-center px-6 py-3 bg-brand-primary text-brand-cream text-xs uppercase tracking-[0.22em] font-semibold"
                        >
                            {nav.reserveBtnMobile}
                        </a>
                    </nav>
                </div>
            )}
        </header>
    );
};
